/**
 * The Stanza+ value-prop library and the rotation helpers behind the in-feed
 * upsell. Any band variant can carry any prop; the cursor advances evenly per
 * impression so a returning free user sees a different angle each time.
 */

import { isCalendarPersonalizationEnabled } from "src/services/Config.js";

/** Ordered value props. `key` feeds the event_label + upgrade source string. */
export const STANZA_PLUS_PROPS = [
  { key: "limits", headline: "Follow it all", sub: "Add unlimited calendars, all in one feed." },
  { key: "personalize", headline: "Make it yours", sub: "Your settings follow every calendar to every device.", requiresPersonalization: true },
  { key: "reminders", headline: "Never miss the start", sub: "Set alerts for 30 minutes, 2 hours, or a day ahead.", requiresPersonalization: true },
  { key: "exclusive", headline: "Members-only calendars", sub: "Unlock calendars you can't get on the free plan." },
  { key: "speed", headline: "Always up to date", sub: "Faster updates, a last-updated time, and refresh on demand." },
  { key: "history", headline: "Keep the past", sub: "Every calendar keeps its full history, not just what's next." },
  { key: "groups", headline: "Group your calendars", sub: "Bundle what you follow into one clean view." },
  { key: "early", headline: "Get it first", sub: "Early access to everything we ship next." },
];

/** Calendars a free user may follow before Stanza+ is required. */
export const FREE_CALENDAR_LIMIT = 2;

const CURSOR_KEY = "stanza_plus_prop_cursor";
const DISMISS_KEY = "stanza_plus_upsell_dismissed_until";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The props the rotation may show: personalization props only once personalization is live.
 * @returns {typeof STANZA_PLUS_PROPS}
 */
export const activeStanzaPlusProps = () =>
  STANZA_PLUS_PROPS.filter((prop) => !prop.requiresPersonalization || isCalendarPersonalizationEnabled());

/**
 * Returns the next value prop in the even round-robin and advances the stored
 * cursor by one. Falls back to the first prop when storage is unavailable.
 *
 * @param {typeof STANZA_PLUS_PROPS} props
 * @returns {{ key: string, headline: string, sub: string }}
 */
export const nextStanzaPlusProp = (props = activeStanzaPlusProps()) => {
  let cursor = 0;
  try {
    cursor = parseInt(window.localStorage.getItem(CURSOR_KEY) ?? "0", 10) || 0;
    window.localStorage.setItem(CURSOR_KEY, String((cursor + 1) % props.length));
  } catch {
    cursor = 0;
  }
  return props[cursor % props.length];
};

/** True while the user is inside the post-dismiss cooldown window. */
export const isUpsellDismissed = () => {
  try {
    const until = parseInt(window.localStorage.getItem(DISMISS_KEY) ?? "0", 10) || 0;
    return Date.now() < until;
  } catch {
    return false;
  }
};

/** Starts the dismiss cooldown so the upsell stays hidden for a week. */
export const dismissUpsell = () => {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_COOLDOWN_MS));
  } catch {
    /* storage unavailable: dismissal is best-effort, band hides for this mount */
  }
};
